/**
 * Category Tap content: types, level params, round sequence builder from schedule + items.
 * Spec: items one at a time; category switches mid-round; TAP if belongs / SKIP.
 */

export interface CategoryTapItem {
  id: string;
  label: string;
  imageUrl: string;
  categories: string[];
  /**
   * Optional sentence narrated by the in-game avatar overlay (Phase 1
   * voice-over feature). Falls back to the i18n key
   * `games.contentBank.assets.voiceDescriptions.{itemId}` and ultimately
   * to the localized item label when undefined or empty.
   */
  voiceDescription?: string | null;
}

export interface CategoryTapPhase {
  category: string;
  duration: number;
  compound?: { attribute: string; value: string };
}

export interface CategoryTapSchedule {
  scheduleId: string;
  phases: CategoryTapPhase[];
}

/** Backend returns { schedule, schedules, items } for category-tap */
export interface CategoryTapContentResponse {
  /** Single schedule (legacy / back-compat with older servers) */
  schedule?: CategoryTapSchedule;
  /** Full pool of level-matching schedules; client rotates by round index */
  schedules?: CategoryTapSchedule[];
  items?: CategoryTapItem[] | Record<string, unknown>[];
}

export interface CategoryTapLevelParams {
  /** Total items per round (cap; schedule may request fewer) */
  maxItems: number;
  /** Ms per item before auto-advance (0 = no auto-advance) */
  timePerItemMs: number;
  showLabels: boolean;
  showImage: boolean;
}

export interface StimulusStep {
  item: CategoryTapItem;
  phaseIndex: number;
  category: string;
  /** True if item belongs to current phase category */
  belongsToCategory: boolean;
}

export interface CategoryTapRoundContent {
  sequence: StimulusStep[];
  levelParams: CategoryTapLevelParams;
  schedule: CategoryTapSchedule;
}

/** Seeded RNG in [0, 1) for deterministic rounds */
function seededRandom(seed: string): () => number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h << 5) - h + seed.charCodeAt(i);
    h |= 0;
  }
  return () => {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    return h / 0x7fffffff;
  };
}

function shuffleWithSeed<T>(arr: T[], rng: () => number): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Level params from spec table: 1–2 single category 10 items; 3–4 one switch 15; 5–6 two switches 18; 7–8 two–three 20; 9–10 three switches 24, 4s per item */
export function getLevelParams(difficulty: number): CategoryTapLevelParams {
  const level = Math.max(1, Math.min(10, difficulty));
  if (level <= 2) {
    return {
      maxItems: 10,
      timePerItemMs: 0,
      showLabels: true,
      showImage: true,
    };
  }
  if (level <= 4) {
    return {
      maxItems: 15,
      timePerItemMs: 0,
      showLabels: true,
      showImage: true,
    };
  }
  if (level <= 6) {
    return {
      maxItems: 18,
      timePerItemMs: 0,
      showLabels: true,
      showImage: true,
    };
  }
  if (level <= 8) {
    return {
      maxItems: 20,
      timePerItemMs: 0,
      showLabels: true,
      showImage: true,
    };
  }
  return {
    maxItems: 24,
    timePerItemMs: 4000,
    showLabels: true,
    showImage: true,
  };
}

function normalizeItem(raw: Record<string, unknown>): CategoryTapItem {
  const id = (raw.id ?? raw.itemId ?? "") as string;
  const voice = raw.voiceDescription;
  return {
    id,
    label: (raw.label as string) ?? "",
    imageUrl: (raw.imageUrl as string) ?? "",
    categories: Array.isArray(raw.categories) ? (raw.categories as string[]) : [],
    voiceDescription: typeof voice === "string" ? voice : null,
  };
}

function itemBelongsToCategory(item: CategoryTapItem, category: string): boolean {
  return item.categories.some((c) => c.toLowerCase() === category.toLowerCase());
}

/**
 * Build the round sequence from schedule and item pool.
 * For each phase: pick `duration` items (mix of in-category and distractors), shuffle within phase.
 */
export function buildRoundSequence(
  schedule: CategoryTapSchedule,
  items: CategoryTapItem[],
  seed: string,
  roundIndex: number
): StimulusStep[] {
  const rng = seededRandom(`${seed}-${roundIndex}`);
  const steps: StimulusStep[] = [];
  const usedIds = new Set<string>();

  for (let phaseIndex = 0; phaseIndex < schedule.phases.length; phaseIndex++) {
    const phase = schedule.phases[phaseIndex];
    const category = phase.category;
    const duration = phase.duration;

    const inCategory = items.filter(
      (i) => !usedIds.has(i.id) && itemBelongsToCategory(i, category)
    );
    const notInCategory = items.filter(
      (i) => !usedIds.has(i.id) && !itemBelongsToCategory(i, category)
    );

    const numTarget = Math.max(
      1,
      Math.min(inCategory.length, Math.floor(duration * (0.5 + rng() * 0.3)))
    );
    const numDistractor = Math.min(duration - numTarget, notInCategory.length);
    const totalThisPhase = numTarget + numDistractor;

    const chosenIn = shuffleWithSeed(inCategory, rng).slice(0, numTarget);
    const chosenOut = shuffleWithSeed(notInCategory, rng).slice(0, numDistractor);
    const phaseSteps = shuffleWithSeed([...chosenIn, ...chosenOut], rng);

    for (const item of phaseSteps) {
      usedIds.add(item.id);
      steps.push({
        item,
        phaseIndex,
        category,
        belongsToCategory: itemBelongsToCategory(item, category),
      });
    }
  }

  return steps;
}

function isCategoryTapContentResponse(
  data: unknown
): data is CategoryTapContentResponse {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  const schedule = d.schedule as Record<string, unknown> | undefined;
  const schedules = d.schedules as unknown;
  const hasSingle =
    !!schedule && Array.isArray((schedule as Record<string, unknown>)?.phases);
  const hasPool = Array.isArray(schedules) && schedules.length > 0;
  return hasSingle || hasPool;
}

/**
 * Collapse consecutive phases that share the same `category` into a single
 * phase whose duration is the sum. The schedule bank uses a `compound`
 * sub-filter (e.g. `{ attribute: "medium", value: "air" }`) to distinguish
 * two phases whose top-level category is identical, but the client does not
 * yet interpret `compound` when matching items. Without this collapse the UI
 * would announce a "new category" between phases whose items are
 * indistinguishable, which is what the user sees at level 5 with schedules
 * like `cts_027` (vehicles → vehicles).
 *
 * Once compound support lands client-side, this becomes a no-op because
 * adjacent phases will no longer share the same effective filter key.
 */
function collapseConsecutiveSameCategory(
  phases: CategoryTapPhase[],
): CategoryTapPhase[] {
  const collapsed: CategoryTapPhase[] = [];
  for (const phase of phases) {
    const last = collapsed[collapsed.length - 1];
    const sameCategory =
      !!last &&
      last.category.toLowerCase() === phase.category.toLowerCase() &&
      // Only merge when neither side carries a compound filter we'd honor.
      // Keeping a strict "both lack compound" check here means we can start
      // respecting compound later without changing this function.
      !last.compound &&
      !phase.compound;
    if (sameCategory) {
      last.duration += phase.duration;
    } else {
      collapsed.push({ ...phase });
    }
  }
  return collapsed;
}

function normalizeSchedule(
  raw: Record<string, unknown> | undefined,
): CategoryTapSchedule | null {
  if (!raw) return null;
  const rawPhases = raw.phases as CategoryTapPhase[] | undefined;
  if (!Array.isArray(rawPhases) || rawPhases.length === 0) return null;
  // Strip compound filters the client can't enforce yet. Leaving them on
  // would make the UI announce a switch between phases whose items are
  // identical from the matcher's point of view (e.g. two "vehicles" phases
  // separated only by `compound.medium`).
  const phases = rawPhases.map((p) => ({
    category: p.category,
    duration: p.duration,
  }));
  const collapsed = collapseConsecutiveSameCategory(phases);
  if (collapsed.length === 0) return null;
  return {
    scheduleId: (raw.scheduleId as string) ?? "fallback",
    phases: collapsed,
  };
}

/**
 * Resolve round content from API response. Normalizes items (id/itemId), picks
 * the schedule for the given round (rotating through `schedules` when the
 * server provides a pool, falling back to a single `schedule` for older
 * servers), and builds the item sequence.
 */
export function resolveRoundContent(
  contentData: unknown,
  contentError: unknown,
  contentSeed: string,
  roundIndex: number,
  levelParams: CategoryTapLevelParams,
  sessionSeed?: string
): CategoryTapRoundContent | null {
  const effectiveSeed = sessionSeed
    ? (contentSeed ? `${contentSeed}-${sessionSeed}` : sessionSeed)
    : contentSeed || `local-${roundIndex}-${Date.now()}`;

  if (contentError || !isCategoryTapContentResponse(contentData)) {
    return null;
  }

  const rawItems = contentData.items;
  if (!Array.isArray(rawItems) || rawItems.length < 5) {
    return null;
  }

  const rawSchedules = Array.isArray(contentData.schedules)
    ? (contentData.schedules as unknown as Record<string, unknown>[])
    : [];
  const normalizedPool = rawSchedules
    .map(normalizeSchedule)
    .filter((s): s is CategoryTapSchedule => s !== null);

  let schedule: CategoryTapSchedule | null = null;
  if (normalizedPool.length > 0) {
    schedule = normalizedPool[roundIndex % normalizedPool.length];
  } else {
    schedule = normalizeSchedule(
      contentData.schedule as Record<string, unknown> | undefined,
    );
  }

  if (!schedule) {
    return null;
  }

  const items = (rawItems as Record<string, unknown>[]).map(normalizeItem);

  const sequence = buildRoundSequence(
    schedule,
    items,
    effectiveSeed,
    roundIndex
  ).slice(0, levelParams.maxItems);

  if (sequence.length === 0) {
    return null;
  }

  return {
    sequence,
    levelParams,
    schedule,
  };
}

/**
 * Per-item response record. `reactionTime` is captured only for decisions
 * the player made themselves (tap or skip). Missed items (auto-advance on
 * timer expiry) carry `reactionTime: null` and are excluded from the
 * reaction-time average.
 */
export interface CategoryTapItemResponse {
  tapped: boolean;
  missed?: boolean;
  reactionTime?: number | null;
}

/**
 * Compute accuracy (correct / total) and switchScore (accuracy on first 3 items after each category switch).
 */
export function computeRoundMetrics(
  sequence: StimulusStep[],
  responses: CategoryTapItemResponse[]
): { accuracy: number; switchScore: number; missedItems: number } {
  let correct = 0;
  let total = 0;
  let missedItems = 0;
  const postSwitchScores: number[] = [];
  let phaseStartIndex = 0;

  for (let i = 0; i < sequence.length; i++) {
    const step = sequence[i];
    const resp = responses[i];
    if (!resp) {
      missedItems++;
      total++;
      continue;
    }
    if (resp.missed) {
      missedItems++;
      total++;
      continue;
    }
    const expectedTap = step.belongsToCategory;
    const actualTap = resp.tapped;
    if (expectedTap === actualTap) correct++;
    total++;

    // Detect phase switch: next step has different phaseIndex
    const nextStep = sequence[i + 1];
    if (nextStep && nextStep.phaseIndex !== step.phaseIndex) {
      const window = responses.slice(i + 1, Math.min(i + 4, responses.length));
      const windowSteps = sequence.slice(i + 1, i + 1 + window.length);
      let windowCorrect = 0;
      let windowTotal = 0;
      for (let w = 0; w < window.length && w < windowSteps.length; w++) {
        if (window[w]?.missed) {
          windowTotal++;
          continue;
        }
        if (window[w]) {
          windowTotal++;
          const exp = windowSteps[w].belongsToCategory;
          const act = window[w].tapped;
          if (exp === act) windowCorrect++;
        }
      }
      if (windowTotal > 0) {
        postSwitchScores.push(windowCorrect / windowTotal);
      }
    }
  }

  const accuracy = total > 0 ? correct / total : 0;
  const switchScore =
    postSwitchScores.length > 0
      ? postSwitchScores.reduce((a, b) => a + b, 0) / postSwitchScores.length
      : accuracy;

  return { accuracy, switchScore, missedItems };
}

/**
 * Mirrors the error count the component submits as `errorCount` to the
 * backend. Kept as a named helper so tests can pin its behavior:
 * `length - round(accuracy * length) + missedItems`, floored at 0.
 *
 * Note: `missedItems` already reduces `accuracy` (it counts in `total` but
 * not in `correct`), so missed steps effectively end up counted twice in
 * this total. Backend domain scoring is driven by `accuracy` and
 * `switchScore`, so this impacts only the client-side error pill display.
 */
export function computeCategoryTapErrorCount(
  sequenceLength: number,
  accuracy: number,
  missedItems: number,
): number {
  const rawErrors =
    sequenceLength - Math.round(accuracy * sequenceLength) + missedItems;
  return Math.max(0, rawErrors);
}

/**
 * Average reaction time (ms) across the responses the player actually
 * committed. Missed items (auto-advance) and rows without a recorded
 * `reactionTime` are excluded from both numerator and denominator.
 *
 * Returns `{ avgReactionMs: 0, reactionTimeMs: null }` if no reaction time
 * was captured, matching the shape used by the other games so the payload
 * can forward `reactionTimeMs` directly.
 */
export function computeCategoryTapReactionTime(
  responses: CategoryTapItemResponse[],
): { avgReactionMs: number; reactionTimeMs: number | null } {
  const values = responses
    .filter((r) => !r.missed && typeof r.reactionTime === "number")
    .map((r) => r.reactionTime as number);
  if (values.length === 0) {
    return { avgReactionMs: 0, reactionTimeMs: null };
  }
  const avgReactionMs =
    values.reduce((a, b) => a + b, 0) / values.length;
  return { avgReactionMs, reactionTimeMs: Math.round(avgReactionMs) };
}
